import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, format, addDays } from 'date-fns';

const parseDateSafe = (d) => {
    if (!d) return new Date();
    if (typeof d === 'string' || typeof d === 'number') return new Date(d);
    if (d.$date) return new Date(d.$date);
    return new Date(d);
};

const CalendarHeader = ({ selectedDate, setSelectedDate, agendamentos, bloqueios, viewMode, setViewMode, timezone, horaInicio = 7, horaFim = 22 }) => {
    
    const viewModes = {
        semanal: { label: 'Semanal', next: 'quinzenal' },
        quinzenal: { label: 'Quinzenal', next: 'mensal' },
        mensal: { label: 'Mensal', next: 'semanal' }
    };

    const handleViewChange = () => {
        setViewMode(viewModes[viewMode].next);
    };

    const calendarDays = useMemo(() => {
        const start = startOfWeek(viewMode === 'mensal' ? startOfMonth(selectedDate) : selectedDate, { weekStartsOn: 0 });
        let end;
        if (viewMode === 'semanal') {
            end = endOfWeek(start, { weekStartsOn: 0 });
        } else if (viewMode === 'quinzenal') {
            end = addDays(start, 13);
        } else {
            end = endOfWeek(endOfMonth(selectedDate), { weekStartsOn: 0 });
        }
        return eachDayOfInterval({ start, end }).map(day => ({
            date: day,
            isCurrentMonth: day.getMonth() === selectedDate.getMonth()
        }));
    }, [selectedDate, viewMode]);

    const eventsByDate = useMemo(() => {
        const map = new Map();
        const timeZone = timezone || 'America/Sao_Paulo';
        const capacityMinutes = Math.max(1, (horaFim - horaInicio) * 60);

        const markDays = (event, field) => {
            if (!event || !event.dataInicio) return;
            const inicio = parseDateSafe(event.dataInicio);
            const fim    = event.dataFim ? parseDateSafe(event.dataFim) : inicio;

            const inicioStr = formatInTimeZone(inicio, timeZone, 'yyyy-MM-dd');
            const fimStr    = formatInTimeZone(fim,    timeZone, 'yyyy-MM-dd');

            let current = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate());
            const endDay = new Date(fim.getFullYear(),   fim.getMonth(),   fim.getDate());

            while (current <= endDay) {
                const dateStr = formatInTimeZone(current, timeZone, 'yyyy-MM-dd');
                if (!map.has(dateStr)) map.set(dateStr, { agendamentos: 0, bloqueios: 0, bookedMinutes: 0 });

                const entry = map.get(dateStr);
                entry[field] += 1;

                // Calcula sobreposição do evento com o horário de trabalho deste dia
                const workStart = new Date(current.getFullYear(), current.getMonth(), current.getDate(), horaInicio, 0, 0);
                const workEnd   = new Date(current.getFullYear(), current.getMonth(), current.getDate(), horaFim,    0, 0);
                const overlapStart = Math.max(inicio.getTime(), workStart.getTime());
                const overlapEnd   = Math.min(fim.getTime(),   workEnd.getTime());
                if (overlapEnd > overlapStart) {
                    entry.bookedMinutes += (overlapEnd - overlapStart) / 60000;
                }

                if (inicioStr === fimStr) break;
                current = addDays(current, 1);
            }
        };

        (agendamentos || []).forEach(e => markDays(e, 'agendamentos'));
        (bloqueios    || []).forEach(e => markDays(e, 'bloqueios'));

        // Calcula % de ocupação para cada dia
        map.forEach(data => {
            data.occupancyPct = Math.min(100, Math.round(data.bookedMinutes / capacityMinutes * 100));
        });

        return map;
    }, [agendamentos, bloqueios, timezone, horaInicio, horaFim]);

    const getEventCountsForDate = (date) => {
        const tz = timezone || 'America/Sao_Paulo';
        const dateStr = formatInTimeZone(date, tz, 'yyyy-MM-dd');
        return eventsByDate.get(dateStr) || { agendamentos: 0, bloqueios: 0 };
    };
    
    const changeDate = (direction) => {
        const newDate = new Date(selectedDate);
        if (viewMode === 'mensal') {
            newDate.setDate(1);
            newDate.setMonth(newDate.getMonth() + direction);
        } else {
            const step = viewMode === 'semanal' ? 7 : 14;
            newDate.setDate(newDate.getDate() + (step * direction));
        }
        setSelectedDate(newDate);
    };

    return (
        <div className={`agenda-header view-${viewMode}`} style={{ position: 'relative', zIndex: 1 }}>
            <div className="header-controls">
                <button onClick={() => changeDate(-1)}><ChevronLeft size={24}/></button>
                <div className="current-month-year">{selectedDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</div>
                <div className="view-switcher">
                    <button onClick={handleViewChange} className="view-switcher-button">
                        {viewModes[viewMode].label}
                    </button>
                    <button onClick={() => changeDate(1)}><ChevronRight size={24}/></button>
                </div>
            </div>
            <div className="days-strip">
                {['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'].map(dayName => (
                    <div key={dayName} className="day-name-header">{dayName}</div>
                ))}
                
                {calendarDays.map(({ date, isCurrentMonth }) => {
                    const counts = getEventCountsForDate(date);
                    const dateStr = formatInTimeZone(date, timezone || 'America/Sao_Paulo', 'yyyy-MM-dd');
                    const todayStr = formatInTimeZone(new Date(), timezone || 'America/Sao_Paulo', 'yyyy-MM-dd');
                    const selectedStr = formatInTimeZone(selectedDate, timezone || 'America/Sao_Paulo', 'yyyy-MM-dd');
                    
                    const isToday = dateStr === todayStr;
                    const isSelected = dateStr === selectedStr;
                    
                    const isLotado = counts.occupancyPct >= 100;

                    let dayClass = 'day-button';
                    if (isToday)    dayClass += ' is-today';
                    if (isSelected) dayClass += ' is-active';
                    if (isLotado)   dayClass += ' is-lotado';
                    if (!isCurrentMonth && viewMode === 'mensal') dayClass += ' is-other-month';

                    const pctClass = counts.occupancyPct >= 100 ? 'pct-full'
                                   : counts.occupancyPct >= 80  ? 'pct-high'
                                   : counts.occupancyPct >= 50  ? 'pct-medium'
                                   : 'pct-low';

                    return (
                        <button key={dateStr} className={dayClass} onClick={() => setSelectedDate(date)}>
                            <span className="day-number">{format(date, 'd')}</span>

                            {(counts.agendamentos > 0 || counts.bloqueios > 0) && (
                                <div className="day-event-count-wrapper">
                                    {counts.agendamentos > 0 && (
                                        <span className="event-count-item event-count--agendamento">
                                            {counts.agendamentos}
                                        </span>
                                    )}
                                    {counts.bloqueios > 0 && (
                                        <span className="event-count-item event-count--bloqueio">
                                            {counts.bloqueios}
                                        </span>
                                    )}
                                </div>
                            )}

                            {counts.occupancyPct > 0 && (
                                isLotado
                                    ? <span className="day-lotado-badge">Lotado</span>
                                    : <span className="day-pct-label">{counts.occupancyPct}%</span>
                            )}

                            {counts.occupancyPct > 0 && (
                                <div className="day-occupancy-track">
                                    <div
                                        className={`day-occupancy-fill ${pctClass}`}
                                        style={{ width: `${counts.occupancyPct}%` }}
                                    />
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default CalendarHeader;